/**
 * Smossie Puddles — shared browser runtime.
 *
 * Everything here is plain custom elements and event delegation. No build step,
 * no framework: the theme editor re-renders sections as raw HTML, and custom
 * elements upgrade themselves when that happens.
 */

(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------- helpers */

  const Smossie = {
    /** Root-relative URL that respects the active locale/market. */
    routes: window.SmossieRoutes || {},

    money(cents) {
      const format = window.SmossieMoneyFormat || '€{{amount}}';
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return format.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, amount);
    },

    /** Fetches a section's HTML so we can swap a fragment in place. */
    async fetchSection(sectionId, url = window.location.pathname) {
      const response = await fetch(`${url}?section_id=${sectionId}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Section ${sectionId} failed: ${response.status}`);
      return response.text();
    },

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    },
  };

  window.Smossie = Smossie;

  /* -------------------------------------------------------------- drawer */

  /**
   * Generic slide-over. Opened by any element with
   * `data-drawer-open="<drawer id>"`, closed by the scrim, the close button or
   * Escape. Focus is trapped while open and restored on close.
   */
  class SmossieDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
        const link = event.target.closest('.mobile-nav__link');
        if (link && this.isOpen) this.navigateFromMenu(link, event);
      });
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    navigateFromMenu(link, event) {
      const rawHref = link.getAttribute('href');
      if (!rawHref) return;

      const destination = new URL(rawHref, window.location.href);
      if (destination.origin !== window.location.origin || !destination.hash) {
        this.close();
        return;
      }

      event.preventDefault();
      let targetId = decodeURIComponent(destination.hash.replace(/^#\/?/, ''));
      const legacyAnchors = {
        'how-it-works': 'one-suit',
        'colour-sizes': 'colours',
        'size-colours': 'colours',
        'size-and-colours': 'colours',
        why: 'one-suit',
        'why-it-works': 'one-suit',
      };
      targetId = legacyAnchors[targetId] || targetId;
      let target = document.getElementById(targetId);
      const isHashOnlyLink = rawHref.startsWith('#');
      const rootPath = Smossie.routes.root || '/';

      /*
       * Section anchors belong to the homepage. A hash-only Shopify menu link
       * resolves against the current product URL, so on product pages it would
       * incorrectly become /products/foo#why-it-works. Resolve it against the
       * locale-aware root instead.
       */
      if (!target && isHashOnlyLink && destination.pathname !== rootPath) {
        destination.pathname = rootPath;
        target = null;
      }

      this.close();

      if (!target) {
        window.location.assign(`${destination.pathname}${destination.search}#${targetId}`);
        return;
      }

      window.setTimeout(() => {
        history.pushState(null, '', `#${targetId}`);
        target.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
      }, REDUCED_MOTION ? 0 : 320);
    }

    open() {
      if (this.isOpen && !this.hasAttribute('data-closing')) return;
      clearTimeout(this.closeTimer);
      this.opener = document.activeElement;
      this.removeAttribute('data-closing');
      this.setAttribute('open', '');
      this.opener?.setAttribute?.('aria-expanded', 'true');
      document.addEventListener('keydown', this.handleKeydown);
      const focusTarget = this.querySelector('[data-drawer-autofocus], [data-drawer-close]');
      focusTarget?.focus();
      Smossie.emit('smossie:drawer:open', { id: this.id });
    }

    close() {
      if (!this.isOpen || this.hasAttribute('data-closing')) return;
      this.setAttribute('data-closing', '');
      this.opener?.setAttribute?.('aria-expanded', 'false');
      document.removeEventListener('keydown', this.handleKeydown);
      this.closeTimer = window.setTimeout(() => {
        this.removeAttribute('open');
        this.removeAttribute('data-closing');
        this.opener?.focus?.();
        Smossie.emit('smossie:drawer:close', { id: this.id });
      }, REDUCED_MOTION ? 0 : 320);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [
        ...this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('smossie-drawer', SmossieDrawer);

  const menuTitleColours = [
    'var(--color-cream-500)',
    '#f6c1d1',
    '#afcbeb',
    '#ff6750',
    '#f7e08c',
    '#b3e6d0',
    '#cfbde6',
  ];
  let menuColourTimer;
  let menuColourIndex = 0;

  function cycleMenuTitleColour() {
    const title = document.querySelector('#mobile-menu .drawer__head .wordmark__text');
    if (!title) return;
    title.style.color = menuTitleColours[menuColourIndex];
    menuColourIndex = (menuColourIndex + 1) % menuTitleColours.length;
    menuColourTimer = window.setTimeout(
      cycleMenuTitleColour,
      menuColourIndex === 1 ? 5000 : 3000,
    );
  }

  document.addEventListener('smossie:drawer:open', (event) => {
    if (event.detail?.id !== 'mobile-menu') return;
    clearTimeout(menuColourTimer);
    menuColourIndex = 0;
    cycleMenuTitleColour();
  });

  document.addEventListener('smossie:drawer:close', (event) => {
    if (event.detail?.id !== 'mobile-menu') return;
    clearTimeout(menuColourTimer);
    menuColourTimer = null;
    const title = document.querySelector('#mobile-menu .drawer__head .wordmark__text');
    if (title) title.style.color = menuTitleColours[0];
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer) return;
    event.preventDefault();
    drawer.open();
  });

  /* --------------------------------------------------------- scroll reveal */

  const revealObserver = REDUCED_MOTION
    ? null
    : new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );

  function observeReveals(root = document) {
    if (!revealObserver) return;
    root.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => revealObserver.observe(el));
  }

  /* ------------------------------------------------------- sticky header */

  function watchHeaderShadow() {
    const header = document.querySelector('[data-sticky-header]');
    if (!header || header.dataset.shadowObserved) return;
    header.dataset.shadowObserved = 'true';
    const update = () => header.classList.toggle('is-stuck', window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------------------------------------ dismissal */

  /**
   * Remembers a dismissal for N days. Used by the floating newsletter card so
   * it does not nag someone who already said no.
   */
  Smossie.snooze = {
    key: (name) => `smossie:snooze:${name}`,
    isActive(name) {
      try {
        const until = window.localStorage.getItem(this.key(name));
        return until !== null && Number(until) > Date.now();
      } catch {
        return false;
      }
    },
    set(name, days) {
      try {
        window.localStorage.setItem(this.key(name), String(Date.now() + days * 864e5));
      } catch {
        /* private mode — the card simply reappears next visit */
      }
    },
  };

  /* ---------------------------------------------------------- newsletter */

  const KLAVIYO_REVISION = '2024-10-15';

  /**
   * Progressive enhancement over Shopify's native customer form. Without keys
   * or without JS the form posts to Shopify as usual; with keys we also
   * subscribe the address in Klaviyo and keep the visitor on the page.
   */
  async function subscribeToKlaviyo(companyId, listId, email) {
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email } } },
        },
      },
    };

    if (listId) {
      body.data.relationships = { list: { data: { type: 'list', id: listId } } };
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
      }
    );

    if (!response.ok && response.status !== 202) {
      throw new Error(`Klaviyo responded ${response.status}`);
    }
  }

  function initNewsletter(root = document) {
    root.querySelectorAll('[data-newsletter]').forEach((wrapper) => {
      const companyId = wrapper.dataset.klaviyoCompany;
      if (!companyId || wrapper.dataset.enhanced) return;
      wrapper.dataset.enhanced = 'true';

      const form = wrapper.querySelector('form');
      const status = wrapper.querySelector('[data-newsletter-status]');
      const submit = wrapper.querySelector('[type="submit"]');

      form.addEventListener('submit', async (event) => {
        const email = form.querySelector('input[type="email"]').value.trim();
        if (!email) return;

        event.preventDefault();
        submit.disabled = true;
        status.hidden = false;
        status.dataset.state = 'pending';
          status.textContent = wrapper.dataset.pendingLabel || '…';

        try {
          await subscribeToKlaviyo(companyId, wrapper.dataset.klaviyoList, email);
          status.dataset.state = 'success';
          status.textContent = wrapper.dataset.successLabel || '✓';
          form.reset();
          Smossie.emit('smossie:newsletter:subscribed', { email });
        } catch (error) {
          console.error(error);
          // Fall back to the native Shopify submit rather than losing the signup.
          form.submit();
          return;
        } finally {
          submit.disabled = false;
        }
      });
    });
  }

  /* -------------------------------------------- mobile discovery cards */

  function initMessCards(root = document) {
    root.querySelectorAll('[data-mess-cards]').forEach((group) => {
      if (group.dataset.mobileCardsInitialised) return;
      group.dataset.mobileCardsInitialised = 'true';

      const cards = [...group.querySelectorAll('[data-mess-card]')];
      const mobile = window.matchMedia('(width < 60rem)');
      let frame = 0;

      const update = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!mobile.matches) {
            cards.forEach((card) => card.classList.remove('is-active'));
            return;
          }

          const viewportCentre = window.innerHeight / 2;
          const nearest = cards.reduce((winner, card) => {
            const box = card.getBoundingClientRect();
            const distance = Math.abs(box.top + box.height / 2 - viewportCentre);
            return !winner || distance < winner.distance ? { card, distance } : winner;
          }, null)?.card;

          cards.forEach((card) => card.classList.toggle('is-active', card === nearest));
        });
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      mobile.addEventListener('change', update);
      update();
    });
  }

  /* --------------------------------------------- shuffled size colours */

  function initSizeColours(root = document) {
    root.querySelectorAll('[data-size-colours]').forEach((grid) => {
      if (grid.dataset.coloursInitialised) return;
      grid.dataset.coloursInitialised = 'true';

      const colours = (grid.dataset.sizeColours || '').split(',').filter(Boolean);
      const shuffled = [...colours];
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const target = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
      }

      grid.querySelectorAll('.swatches__size').forEach((size, index) => {
        if (shuffled.length) size.style.setProperty('--size-colour', shuffled[index % shuffled.length]);
      });
    });
  }

  /* ------------------------------------------------------ floating card */

  function initNewsletterFloat() {
    const card = document.querySelector('[data-newsletter-float]');
    if (!card || card.dataset.initialised) return;
    card.dataset.initialised = 'true';

    const snoozeDays = Number(card.dataset.snooze) || 30;
    const delay = (Number(card.dataset.delay) || 0) * 1000;
    const scrollButton = document.querySelector('[data-scroll-top]');
    const syncStack = () => {
      const visible = !card.hidden;
      scrollButton?.classList.toggle('is-stacked', visible);
      if (visible) {
        scrollButton?.style.setProperty('--newsletter-float-height', `${card.offsetHeight}px`);
      }
    };
    const showCard = () => {
      card.classList.remove('is-leaving');
      card.hidden = false;
      requestAnimationFrame(syncStack);
    };
    const hideCard = () => {
      card.classList.add('is-leaving');
      window.setTimeout(() => {
        card.hidden = true;
        card.classList.remove('is-leaving');
        syncStack();
      }, REDUCED_MOTION ? 0 : 300);
    };

    card.querySelector('[data-newsletter-float-close]')?.addEventListener('click', () => {
      hideCard();
      Smossie.snooze.set('newsletter', snoozeDays);
    });

    document.addEventListener('smossie:newsletter:subscribed', () => {
      Smossie.snooze.set('newsletter', 365);
      window.setTimeout(hideCard, 3000);
    });

    new ResizeObserver(syncStack).observe(card);
    if (Smossie.snooze.isActive('newsletter')) return;
    window.setTimeout(showCard, delay);
  }

  /* ------------------------------------------------------ marquee hover speed */

  /**
   * Slow the existing CSS animation without changing its duration. Updating
   * playbackRate preserves the current playhead, so hovering never snaps the
   * text back to the beginning of a new, slower animation.
   */
  function initMarquee(root) {
    if (REDUCED_MOTION) return;

    root.querySelectorAll?.('.marquee').forEach((marquee) => {
      if (marquee.dataset.speedInitialised) return;
      marquee.dataset.speedInitialised = 'true';

      let frame = 0;
      let targetRate = 1;
      let rate = 1;

      const setRate = (nextRate) => {
        targetRate = nextRate;
        cancelAnimationFrame(frame);

        const started = performance.now();
        const startRate = rate;
        const duration = 700;

        const ease = (progress) => 1 - (1 - progress) ** 3;
        const animate = (now) => {
          const progress = Math.min(1, (now - started) / duration);
          rate = startRate + (targetRate - startRate) * ease(progress);
          const animation = [...marquee.getAnimations()].find(
            (candidate) => candidate.animationName === 'marquee-slide',
          );

          if (animation) animation.updatePlaybackRate(rate);
          if (progress < 1) frame = requestAnimationFrame(animate);
        };

        frame = requestAnimationFrame(animate);
      };

      marquee.addEventListener('mouseenter', () => setRate(0.57));
      marquee.addEventListener('mouseleave', () => setRate(1));
    });
  }

  /* ---------------------------------------------------------- section art */

  /**
   * Derive a stable decorative position from the section instance ID. The
   * artwork can feel varied across the page without jumping to a new position
   * on every render or editor refresh.
   */
  function initSectionArt(root) {
    const artLayers = [
      ...(root.matches?.('[data-section-art]') ? [root] : []),
      ...(root.querySelectorAll?.('[data-section-art]') || []),
    ];

    artLayers.forEach((art) => {
      if (art.dataset.artPositionInitialised) return;
      art.dataset.artPositionInitialised = 'true';

      let hash = 2166136261;
      for (const character of art.dataset.artSeed || 'section-art') {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, 16777619);
      }

      const random = () => {
        hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
        hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
        return ((hash ^= hash >>> 16) >>> 0) / 4294967296;
      };

      const isRight = art.dataset.artDirection === 'right';
      const isNarrow = window.matchMedia('(width < 60rem)').matches;
      const isCluster = art.dataset.artLayout === 'cluster';
      const origin = isCluster ? (isNarrow ? 50 : isRight ? 68 : 32) : isRight ? 64 : 20;
      art.style.setProperty('--section-art-x', `${origin + (isCluster ? 0 : random() * 18)}%`);
      art.style.setProperty('--section-art-y', `${isCluster ? 50 : 15 + random() * 70}%`);
      art.style.setProperty('--section-art-rotation', `${-18 + random() * 36}deg`);

      if (isCluster) {
        const angles = isNarrow ? [-90, -18, 42, 126, 204] : [-90, -22, 38, 126, 208];
        const radii = isNarrow ? [0, 27, 43, 56, 62] : [0, 30, 48, 62, 72];
        art.querySelectorAll('.section-art__icon').forEach((icon, index) => {
          const angle = (angles[index] * Math.PI) / 180;
          const radius = radii[index];
          const jitter = (random() - 0.5) * (isNarrow ? 5 : 8);
          icon.style.setProperty('--icon-x', `${50 + Math.cos(angle) * radius + jitter}%`);
          icon.style.setProperty('--icon-y', `${50 + Math.sin(angle) * radius + jitter}%`);
          icon.style.setProperty('--icon-size', `${22 + random() * 8}%`);
          icon.style.setProperty('--icon-rotation', `${-24 + random() * 48}deg`);
          icon.style.setProperty('--icon-scale', `${0.85 + random() * 0.35}`);
        });
      }
    });
  }

  /* -------------------------------------------------------- one-suit layers */

  function initOneSuitLayers(root) {
    const frames = [
      ...(root.matches?.('.onesuit__frame') ? [root] : []),
      ...(root.querySelectorAll?.('.onesuit__frame') || []),
    ];
    const palette = [
      '#F7E08C',
      '#AFCBEB',
      '#F6C1D1',
      '#B3E6D0',
      '#CFBDE6',
      '#FF967C',
    ];

    const chips = [...(root.querySelectorAll?.('.onesuit__chip') || [])];
    const chipColours = [...palette];
    for (let index = chipColours.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [chipColours[index], chipColours[swapIndex]] = [chipColours[swapIndex], chipColours[index]];
    }
    chips.forEach((chip, index) => {
      if (chip.dataset.colourInitialised) return;
      chip.dataset.colourInitialised = 'true';
      chip.style.setProperty('--onesuit-chip-color', chipColours[index % chipColours.length]);
    });

    frames.forEach((frame) => {
      if (frame.dataset.layerColourInitialised) return;
      frame.dataset.layerColourInitialised = 'true';
      const available = palette.filter((colour) => colour !== frame.dataset.previousLayerColour);
      const colour = available[Math.floor(Math.random() * available.length)];
      frame.dataset.previousLayerColour = colour;
      frame.style.setProperty('--onesuit-layer-color', colour);
    });

    const pair = root.querySelectorAll?.('.onesuit__frame');
    if (pair?.length > 1 && pair[0].style.getPropertyValue('--onesuit-layer-color') === pair[1].style.getPropertyValue('--onesuit-layer-color')) {
      const replacement = palette.find(
        (colour) => colour !== pair[0].style.getPropertyValue('--onesuit-layer-color'),
      );
      pair[1].style.setProperty('--onesuit-layer-color', replacement);
    }
  }

  /* ---------------------------------------------------------- scroll to top */

  function initScrollTop(root) {
    const button = root.querySelector?.('[data-scroll-top]') || document.querySelector('[data-scroll-top]');
    if (!button || button.dataset.initialised) return;

    const staleWrap = button.closest('.haptic-tap-wrap--scroll-top');
    if (staleWrap?.parentNode) {
      staleWrap.parentNode.insertBefore(button, staleWrap);
      staleWrap.remove();
      delete button.dataset.hapticSwitch;
    }

    button.dataset.initialised = 'true';

    const updateVisibility = () => {
      button.classList.toggle('is-visible', window.scrollY > 320);
    };

    window.addEventListener('scroll', updateVisibility, { passive: true });
    button.addEventListener('click', () => {
      const mobileMenu = document.getElementById('mobile-menu');
      if (mobileMenu?.hasAttribute('open')) {
        mobileMenu.close();
      }
      window.scrollTo({ top: 0, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    });
    updateVisibility();
  }

  /* ----------------------------------------------------- newsletter trigger */

  function initNewsletterTrigger(root) {
    const triggers = [
      ...(root.matches?.('[data-newsletter-open]') ? [root] : []),
      ...(root.querySelectorAll?.('[data-newsletter-open]') || []),
    ];

    triggers.forEach((trigger) => {
      if (trigger.dataset.initialised) return;
      trigger.dataset.initialised = 'true';
      trigger.addEventListener('click', () => {
        const floating = document.querySelector('[data-newsletter-float]');
        if (floating) {
          floating.hidden = false;
          floating.querySelector('input[type="email"]')?.focus();
          return;
        }

        const section = document.querySelector('.section-signup, .section-finalcta');
        section?.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'center' });
        window.setTimeout(() => section?.querySelector('input[type="email"]')?.focus(), 450);
      });
    });
  }

  /* ----------------------------------------------------------- policy nav */

  function initPolicyNavigation(root) {
    const policy = root.querySelector?.('.shopify-policy__body');
    const title = document.querySelector('.shopify-policy__title');
    if (!policy || !title || document.querySelector('[data-policy-jump-nav]')) return;

    const headings = [...policy.querySelectorAll('h2, h3')].filter((heading) => heading.textContent.trim());
    if (headings.length < 2) return;

    const nav = document.createElement('nav');
    nav.className = 'policy-jump-nav';
    nav.dataset.policyJumpNav = '';
    title.id ||= 'policy-page-title';
    nav.setAttribute('aria-labelledby', title.id);

    headings.forEach((heading, index) => {
      const slug =
        heading.textContent
          .trim()
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') || `section-${index + 1}`;
      heading.id ||= `policy-${slug}`;

      const link = document.createElement('a');
      link.className = 'policy-jump-nav__link';
      link.href = `#${heading.id}`;
      link.innerHTML = `<span class="policy-jump-nav__icon" aria-hidden="true">${index + 1}</span><span>${heading.textContent.trim()}</span><span aria-hidden="true">→</span>`;
      nav.append(link);
    });

    title.insertAdjacentElement('afterend', nav);
  }

  /* ------------------------------------------------------ FAQ accordion */

  function initFaq(root) {
    root.querySelectorAll?.('.faq__item').forEach((item) => {
      if (item.dataset.faqInitialised) return;
      item.dataset.faqInitialised = 'true';
      item.dataset.animated = 'true';

      const summary = item.querySelector('summary');
      if (!summary || REDUCED_MOTION) return;

      summary.addEventListener('click', async (event) => {
        event.preventDefault();
        if (item.dataset.animating) return;
        item.dataset.animating = 'true';

        const startHeight = item.offsetHeight;
        const closing = item.open;
        if (!closing) item.open = true;
        const endHeight = closing ? summary.offsetHeight : item.scrollHeight;

        item.style.overflow = 'hidden';
        const animation = item.animate(
          { height: [`${startHeight}px`, `${endHeight}px`] },
          { duration: 360, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' },
        );

        try {
          await animation.finished;
        } catch {
          // A cancelled animation is harmless; cleanup still restores the item.
        }

        if (closing) item.open = false;
        item.style.removeProperty('height');
        item.style.removeProperty('overflow');
        delete item.dataset.animating;
      });
    });
  }

  /* ------------------------------------------------------ cookie consent */

  function initCookieConsent() {
    const banner = document.querySelector('[data-cookie-consent]');
    if (!banner || banner.dataset.initialised) return;
    banner.dataset.initialised = 'true';
    const storageKey = 'smossie_cookie_consent_v1';

    const setup = () => {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy) return;

      try {
        const stored = JSON.parse(window.localStorage.getItem(storageKey));
        const sixMonths = 180 * 24 * 60 * 60 * 1000;
        if (stored?.savedAt && Date.now() - stored.savedAt < sixMonths) return;
      } catch {
        // Privacy modes can block storage; Shopify's API remains the fallback.
      }

      const palette = ['#F7E08C', '#AFCBEB', '#F6C1D1', '#B3E6D0', '#CFBDE6', '#FF967C'];
      let previousColour = '';
      let colourTimer;
      const cycleLayerColour = () => {
        const choices = palette.filter((colour) => colour !== previousColour);
        previousColour = choices[Math.floor(Math.random() * choices.length)];
        banner.style.setProperty('--cookie-layer-color', previousColour);
      };

      if (privacy.shouldShowBanner()) {
        banner.hidden = false;
        cycleLayerColour();
        colourTimer = window.setInterval(cycleLayerColour, 8000);
      }

      const save = (accepted) => {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify({ accepted, savedAt: Date.now() }));
        } catch {
          // The Customer Privacy API below still persists consent when allowed.
        }
        window.clearInterval(colourTimer);
        banner.hidden = true;

        privacy.setTrackingConsent(
          {
            analytics: accepted,
            marketing: accepted,
            preferences: accepted,
          },
          () => {},
        );
      };

      banner.querySelector('[data-cookie-accept]')?.addEventListener('click', () => save(true));
      banner.querySelector('[data-cookie-decline]')?.addEventListener('click', () => save(false));
    };

    if (window.Shopify?.customerPrivacy) {
      setup();
    } else if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) setup();
      });
    }
  }

  /* -------------------------------------------------------------- haptics */

  const HAPTIC_BUTTON_SELECTOR =
    '.btn--lg, .btn--primary:not(.drawer-head__close), .site-header__burger, .site-header__menu-button, .drawer__head .drawer-head__close[data-drawer-close], .pdp__submit, [data-add-to-cart], .newsletter__submit, .cookie-consent__button--accept';

  const HAPTIC_LAYOUT_CLASSES = ['site-header__burger', 'site-header__menu-button'];

  function shouldAttachHaptics() {
    return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  }

  /**
   * Safari haptics need a direct tap on `input[type=checkbox][switch]`. That
   * input cannot live inside `<button>` (invalid HTML), so we wrap the control
   * and overlay the switch on the wrapper instead.
   */
  function attachHapticSwitch(target) {
    if (target.dataset.hapticSwitch === 'true') return;
    if (target.closest('[data-haptics-disabled]')) return;
    if (target.closest('.haptic-tap-wrap')) return;
    const isAddToCart = target.matches('.pdp__submit, [data-add-to-cart]');
    if (
      !isAddToCart &&
      target.matches('button, input') &&
      (target.disabled || target.getAttribute('aria-disabled') === 'true')
    ) {
      return;
    }

    target.dataset.hapticSwitch = 'true';

    if (target.hasAttribute('data-scroll-top')) return;

    const isDrawerClose = target.matches('.drawer__head .drawer-head__close[data-drawer-close]');

    const wrap = document.createElement('div');
    wrap.className = 'haptic-tap-wrap';
    if (target.classList.contains('btn--full')) {
      wrap.classList.add('haptic-tap-wrap--full');
    }
    if (isDrawerClose) {
      wrap.classList.add('drawer-head__close-wrap');
    }
    HAPTIC_LAYOUT_CLASSES.forEach((className) => {
      if (target.classList.contains(className)) {
        wrap.classList.add(className);
        target.classList.remove(className);
      }
    });

    target.parentNode?.insertBefore(wrap, target);
    wrap.appendChild(target);

    const switchControl = document.createElement('input');
    switchControl.type = 'checkbox';
    switchControl.setAttribute('switch', '');
    switchControl.setAttribute('aria-hidden', 'true');
    switchControl.className = 'smossie-haptic-switch';
    switchControl.tabIndex = -1;
    Object.assign(switchControl.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      margin: '0',
      opacity: '0',
      zIndex: '2',
      cursor: 'pointer',
      touchAction: 'manipulation',
      clipPath: 'inset(0 round 999px)',
    });
    switchControl.style.setProperty('-webkit-appearance', 'auto');
    switchControl.style.setProperty('appearance', 'auto');
    switchControl.style.setProperty('-webkit-tap-highlight-color', 'transparent');

    switchControl.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    switchControl.addEventListener('change', () => {
      switchControl.checked = false;
      if (typeof navigator.vibrate === 'function') {
        navigator.vibrate(12);
      }
      target.click();
    });

    wrap.appendChild(switchControl);

    if (isAddToCart) {
      const syncSwitch = () => {
        switchControl.style.pointerEvents = target.disabled ? 'none' : 'auto';
      };
      syncSwitch();
      new MutationObserver(syncSwitch).observe(target, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }
  }

  function initHaptics(root) {
    if (!shouldAttachHaptics()) return;
    document.documentElement.classList.add('smossie-touch');
    root.querySelectorAll(HAPTIC_BUTTON_SELECTOR).forEach(attachHapticSwitch);
  }

  function initProductCardAccents(root) {
    const palette = ['#F6C1D1', '#AFCBEB', '#FF6750', '#F7E08C', '#B3E6D0', '#CFBDE6'];
    root.querySelectorAll?.('[data-product-card]').forEach((card) => {
      if (card.dataset.accentInitialised) return;
      card.dataset.accentInitialised = 'true';
      let previous = '';
      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const choices = palette.filter((colour) => colour !== previous);
        previous = choices[Math.floor(Math.random() * choices.length)];
        card.style.setProperty('--product-hover-accent', previous);
      });
    });
  }

  /* ------------------------------------------------------------- startup */

  function init(root) {
    observeReveals(root);
    initNewsletter(root);
    initMessCards(root);
    initSizeColours(root);
    initMarquee(root);
    initSectionArt(root);
    initOneSuitLayers(root);
    initScrollTop(root);
    initNewsletterTrigger(root);
    initPolicyNavigation(root);
    initFaq(root);
    initCookieConsent();
    initHaptics(root);
    initProductCardAccents(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    watchHeaderShadow();
    initNewsletterFloat();
  });

  // The theme editor swaps section HTML without a page load.
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    watchHeaderShadow();
  });

  document.addEventListener('smossie:section:replaced', (event) => init(event.detail?.root || document));
})();
